import React from "react";
import { useAppSelector } from "../../../../store/hooks";
import { selectBreakawaySections, selectTotalTonnage } from "../../../../store/selectors/breakawayHullSelectors";
import { formatCurrency, formatNumber } from "../../../../utils/formatting";

/**
 * Breakaway Fuel Summary
 * Sticky footer component for the breakaway fuel step
 */
export const BreakawayFuelSummary: React.FC = () => {
  const sections = useAppSelector(selectBreakawaySections);
  const totalTonnage = useAppSelector(selectTotalTonnage);

  const totals = React.useMemo(() => {
    let totalFuel = 0;
    let totalCost = 0;
    let configuredSections = 0;
    let totalReactionDrive = 0;
    let totalJumpDrive = 0;
    let totalPowerPlant = 0;

    sections.forEach(section => {
      if (section.fuelAllocation && section.fuelAllocation.tons > 0) {
        totalFuel += Math.round(section.fuelAllocation.tons);
        totalCost += section.fuelAllocation.tons * 1000; // 1000 Cr per ton
        totalReactionDrive += Math.round(section.fuelAllocation.requirements.reactionDrive);
        totalJumpDrive += Math.round(section.fuelAllocation.requirements.jumpDrive);
        totalPowerPlant += Math.round(section.fuelAllocation.requirements.powerPlant);
        configuredSections++;
      }
    });

    return {
      totalFuel,
      totalCost,
      totalReactionDrive,
      totalJumpDrive,
      totalPowerPlant,
      configuredSections,
      totalSections: sections.length,
    };
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-800 text-white p-4 shadow-lg">
      <div className="container mx-auto grid grid-cols-[1fr_auto_auto] gap-x-12 items-center">
        {/* Details Row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-gray-400 block">Fuel Status</span>
            <span className={`font-semibold ${
              totals.configuredSections === totals.totalSections
                ? 'text-green-400'
                : 'text-orange-400'
            }`}>
              {totals.configuredSections === totals.totalSections
                ? 'Complete'
                : `${totals.configuredSections}/${totals.totalSections} Sections`
              }
            </span>
          </div>

          {totals.configuredSections > 0 && (
            <>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-400 block">Total Cost</span>
                <span className="font-semibold text-green-400">
                  {formatCurrency(totals.totalCost)}
                </span>
              </div>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-400 block">Sections</span>
                <span className="font-semibold">
                  {sections.length} breakaway • {formatNumber(totalTonnage)} tons
                </span>
              </div>
            </>
          )}
        </div>

        {/* Total Fuel */}
        <div className="text-right">
          <span className="text-gray-400 block text-sm">Total Fuel</span>
          <span className="text-xl font-bold text-blue-400">
            {formatNumber(totals.totalFuel)} tons
          </span>
        </div>

        {/* Section Fuel */}
        <div className="flex flex-col items-center justify-center border-l border-r border-gray-700 px-8">
          <span className="text-gray-400 block text-sm">Section Fuel</span>
          <div className="text-xs space-y-1">
            {sections.map(section => (
              <div key={section.id} className="text-center">
                <span className="text-gray-400">{section.name}: </span>
                <span className={`font-medium ${
                  section.fuelAllocation && section.fuelAllocation.tons > 0
                    ? 'text-blue-400'
                    : 'text-gray-500'
                }`}>
                  {section.fuelAllocation && section.fuelAllocation.tons > 0
                    ? `${formatNumber(Math.round(section.fuelAllocation.tons))} tons`
                    : 'Not configured'
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
